import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapPin, MapPinOff, IndianRupee, FilePlus2, RotateCcw, Download, ArrowUp, ArrowDown, ArrowUpDown, Users, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "./common";
import { CollectionsReconciliation } from "./CollectionsReconciliation";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR, fmtDate, fmtDateAtTime, downloadBlob } from "../lib/api";
import { useLiveCollections } from "../lib/liveCollections";
import { StaffAvatar, StaffLabel, useStaffLabel } from "./StaffAvatar";
import { TerritoryFilterSelects, useTerritoryMasters, territoryLabel } from "./TerritoryFilter";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "./FetchBar";
import { CollectorBreakupRows, useFieldActivityDrilldown } from "./FieldActivityDrilldown";

const today = () => new Date().toISOString().slice(0, 10);

export const EMPTY_AREA = { zoneId: "", supervisorId: "", wardId: "", mohallaId: "" };

/** Phase 127: { zoneId, supervisorId, wardId, mohallaId } -> API query params (only the ids that are set).
 *  Phase 183: `supervisorId` (comma-joined `sup:<id>` / `unassigned:<zone>` keys) travels as `supervisor_id`. */
export function areaParams(area) {
  const a = area || {};
  const out = {};
  if (a.zoneId) out.zone_id = a.zoneId;
  if (a.supervisorId) out.supervisor_id = a.supervisorId;
  if (a.wardId) out.ward_id = a.wardId;
  if (a.mohallaId) out.mohalla_id = a.mohallaId;
  return out;
}

export const areaActive = (area) => !!(area && (area.zoneId || area.supervisorId || area.wardId || area.mohallaId));

/** Phase 128: sortable columns of the collector table. `key` is what the export API expects. */
const SORT_COLUMNS = [
  { key: "collector", field: "collector_name", label: "Collector", numeric: false, align: "left" },
  { key: "visits", field: "visits", label: "Visits", numeric: true, align: "right" },
  { key: "collections_count", field: "collections_count", label: "Receipts", numeric: true, align: "right" },
  { key: "collections_amount", field: "collections_amount", label: "Collected", numeric: true, align: "right" },
  { key: "new_surveys", field: "new_surveys", label: "New surveys", numeric: true, align: "right" },
  { key: "reassessments", field: "reassessments", label: "Reassessments", numeric: true, align: "right" },
];
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** Stable sort of the rows by one column; `sort` = { key, dir } or null (server order). */
export function sortRows(rows, sort) {
  if (!sort?.key) return rows;
  const col = SORT_COLUMNS.find((c) => c.key === sort.key);
  if (!col) return rows;
  const mul = sort.dir === "desc" ? -1 : 1;
  return rows.map((r, i) => [r, i]).sort(([a, ia], [b, ib]) => {
    const av = a[col.field], bv = b[col.field];
    const cmp = col.numeric ? (Number(av) || 0) - (Number(bv) || 0) : collator.compare(String(av || ""), String(bv || ""));
    return cmp !== 0 ? cmp * mul : ia - ib;
  }).map(([r]) => r);
}

/** Next state when a header is clicked: default order -> ascending -> descending -> default order. */
export function nextSort(current, col) {
  if (current?.key !== col.key) return { key: col.key, dir: "asc" };
  if (current.dir === "asc") return { key: col.key, dir: "desc" };
  return null;
}

/** "Zone-1 › Ward 12 › Supela" from the client-side masters ("" when nothing is selected). */
export function areaLabel(masters, area) {
  if (!areaActive(area)) return "";
  return territoryLabel(masters, area);     // Phase 176: each level may list several names ("Ward 1, Ward 5")
}

const KPIS = [
  { key: "visits", metric: "visits", label: "Visits", icon: MapPin },
  { key: "collections", metric: "collections", label: "Collections", icon: IndianRupee },
  { key: "new_surveys", metric: "surveys", label: "New surveys", icon: FilePlus2 },
  { key: "reassessments", metric: "reassessments", label: "Reassessments", icon: RotateCcw },
];

const METRIC_TITLES = { visits: "Visits", collections: "Collections", surveys: "New surveys", reassessments: "Reassessments" };

const COLUMNS = {
  visits: [
    // Phase 184: date + the IST clock the collector logged the visit on their device (payment rows: receipt time)
    { h: "Date & time", nowrap: true,
      r: (x) => (
        <span title={x.at ? `${x.at} (IST)` : x.time ? `${x.time} IST` : undefined} data-testid="field-activity-visit-when">
          {fmtDateAtTime(x.date, x.time)}
        </span>
      ) },
    { h: "Collector", r: (x) => x.collector },
    { h: "PIN", r: (x) => x.pin || "—", mono: true }, { h: "Owner", r: (x) => x.owner_name || "—" },
    { h: "Source", r: (x) => x.source }, { h: "Detail", r: (x) => [x.detail, x.notes].filter(Boolean).join(" — ") || "—" },
  ],
  collections: [
    { h: "Date", r: (x) => fmtDate(x.date) }, { h: "Collector", r: (x) => x.collector },
    { h: "Receipt", r: (x) => x.receipt_number, mono: true }, { h: "PIN", r: (x) => x.pin || "—", mono: true },
    { h: "Owner", r: (x) => x.owner_name || "—" }, { h: "Mode", r: (x) => x.payment_mode || "—" },
    { h: "Amount", r: (x) => fmtINR(x.amount), right: true },
  ],
  surveys: [
    { h: "Date", r: (x) => fmtDate(x.date) }, { h: "Collector", r: (x) => x.collector },
    { h: "Survey ID", r: (x) => x.case_id, mono: true }, { h: "Owner", r: (x) => x.owner_name || "—" },
    { h: "Address", r: (x) => x.address || "—" }, { h: "Status", r: (x) => <Badge variant="outline" className="text-[10px] capitalize">{(x.status || "").replace(/_/g, " ")}</Badge> },
  ],
  reassessments: [
    { h: "Date", r: (x) => fmtDate(x.date) }, { h: "Collector", r: (x) => x.collector },
    { h: "Case ID", r: (x) => x.case_id, mono: true }, { h: "PIN", r: (x) => x.pin || "—", mono: true },
    { h: "Owner", r: (x) => x.owner_name || "—" }, { h: "Status", r: (x) => <Badge variant="outline" className="text-[10px] capitalize">{(x.status || "").replace(/_/g, " ")}</Badge> },
  ],
};

/** Drill-down dialog behind any field-activity number.
 *  target = {metric, dateFrom, dateTo, collectorId?, collectorName?, area?, areaLabel?} (Phase 127: area narrows the rows) */
/** Whole days between two YYYY-MM-DD dates (inclusive of both ends); 0 when either is missing / invalid. */
export const spanDays = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(from), b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b - a) / 86400000) + 1;
};

export function FieldActivityDetailDialog({ code, target, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!target) { setData(null); return; }
    setData(null);
    api.get(`/t/${code}/reports/field-activity/detail`, {
      params: { metric: target.metric, date_from: target.dateFrom, date_to: target.dateTo,
                collector_id: target.collectorId || undefined, ...areaParams(target.area) },
    }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ rows: [] }); });
  }, [code, target]);

  const collectorLabel = useStaffLabel(code, { userId: target?.collectorId, name: target?.collectorName });
  const cols = COLUMNS[target?.metric] || [];
  const total = target?.metric === "collections"
    ? (data?.rows || []).reduce((s, r) => s + (r.amount || 0), 0) : null;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-3xl" data-testid="field-activity-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base" data-testid="field-activity-detail-title">
            {METRIC_TITLES[target?.metric]}{target?.collectorName ? ` — ${collectorLabel}` : ""}
            <span className="block text-xs font-normal text-muted-foreground mt-0.5">
              {target && (target.dateFrom === target.dateTo
                ? fmtDate(target.dateFrom)
                : `${fmtDate(target?.dateFrom)} to ${fmtDate(target?.dateTo)}`)}
              {target?.areaLabel && <span data-testid="field-activity-detail-area"> • Area: {target.areaLabel}</span>}
              {data && ` • ${data.rows.length} record${data.rows.length === 1 ? "" : "s"}`}
              {total !== null && data && ` • ${fmtINR(total)}`}
            </span>
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="py-6 text-center" data-testid="field-activity-detail-loading">
            <Spinner />
            <p className="text-xs text-muted-foreground mt-2">
              {spanDays(target?.dateFrom, target?.dateTo) > 92
                ? `Loading records for ${spanDays(target?.dateFrom, target?.dateTo)} days — a long range can take a little while…`
                : "Loading records…"}
            </p>
          </div>
        ) : data.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {target?.areaLabel ? "No records in this period for the selected area." : "No records in this period."}
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border">
            {data.truncated && (
              <p className="text-xs text-muted-foreground bg-muted/40 border-b px-3 py-1.5" data-testid="field-activity-detail-truncated">
                Showing the newest {(data.limit || 0).toLocaleString("en-IN")} records of this range — use Export Excel for the complete list.
              </p>
            )}
            <Table data-testid="field-activity-detail-table">
              <TableHeader>
                <TableRow>{cols.map((c) => <TableHead key={c.h} className={c.right ? "text-right" : ""}>{c.h}</TableHead>)}</TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={i}>
                    {cols.map((c) => (
                      <TableCell key={c.h}
                        className={`text-sm ${c.mono ? "font-mono" : ""} ${c.right ? "text-right tabular-nums font-medium" : ""} ${c.nowrap ? "whitespace-nowrap tabular-nums" : ""}`}>
                        {c.r(r)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Per-collector field activity report for supervisors and municipal admins.
 *  Phase 127 area filter: pass `area` + `onAreaChange` to control it from the page (the page then owns the
 *  Zone/Ward/Mohalla selects); leave them out and the table renders its own cascading selects inline.
 *  Phase 142: `expandable` (Admin MIS Reports) turns every collector row into a 3-level drill-down —
 *  collector → ward-wise → mohalla-wise → individual visit / payment / survey / reassessment records. */
export function FieldActivityTable({ code, testIdPrefix = "field-activity", area: areaProp, onAreaChange, collectionsHref, expandable = false, canExport = true,
  masters: mastersProp }) {
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [ownArea, setOwnArea] = useState(EMPTY_AREA);
  const controlled = areaProp !== undefined;
  const area = controlled ? (areaProp || EMPTY_AREA) : ownArea;
  const setArea = controlled ? (onAreaChange || (() => {})) : setOwnArea;
  // Phase 183: the page may hand down masters that already carry the Supervisor filter options (labels + cascade)
  const ownMasters = useTerritoryMasters(code);
  const masters = mastersProp || ownMasters;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // drill-down target
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState(null); // Phase 128: { key, dir } | null = server order

  // Phase 133: the table loads only when the user clicks Fetch, on the dates / area applied at that moment
  const filters = useMemo(() => ({ dateFrom, dateTo, zoneId: area.zoneId || "", supervisorId: area.supervisorId || "", wardId: area.wardId || "", mohallaId: area.mohallaId || "" }),
    [dateFrom, dateTo, area.zoneId, area.supervisorId, area.wardId, area.mohallaId]);
  const gate = useFetchGate(filters);
  const ap = gate.applied;
  const appliedArea = ap ? { zoneId: ap.zoneId, supervisorId: ap.supervisorId || "", wardId: ap.wardId, mohallaId: ap.mohallaId } : EMPTY_AREA;
  const appliedLabel = areaLabel(masters, appliedArea);
  const load = useCallback(() => {
    if (!ap) return;
    setLoading(true);
    api.get(`/t/${code}/reports/field-activity`, { params: { date_from: ap.dateFrom, date_to: ap.dateTo, ...areaParams(appliedArea) } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); setData({ rows: [], totals: {} }); })
      .finally(() => setLoading(false));
  }, [code, gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = useLiveCollections(code);   // Phase 149: collection columns follow the live ledger
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version, live]);

  // Phase 142: drill-down state + caches, reset whenever a new Fetch is applied
  const drillParams = useMemo(() => ({ date_from: ap?.dateFrom, date_to: ap?.dateTo, ...areaParams(appliedArea) }),
    [gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const drill = useFieldActivityDrilldown(code, drillParams, `${gate.appliedKey}|${gate.version}`);

  const sortParams = sort?.key ? { sort_by: sort.key, sort_dir: sort.dir } : {};
  const rows = useMemo(() => sortRows(data?.rows || [], sort), [data, sort]);
  const sortCol = sort?.key ? SORT_COLUMNS.find((c) => c.key === sort.key) : null;

  // Phase 232: any range is allowed (no 92-day cap) — only "start after end" is refused, with a clear message
  const rangeInvalid = !!dateFrom && !!dateTo && dateFrom > dateTo;
  const days = spanDays(dateFrom, dateTo);
  const appliedDays = spanDays(ap?.dateFrom, ap?.dateTo);
  const longRange = appliedDays > 92;

  const doExport = async () => {
    if (!ap) return;
    setExporting(true);
    const tid = toast.loading(`Preparing the Excel file for ${appliedDays} day${appliedDays === 1 ? "" : "s"}${longRange ? " — a long range can take a little while" : ""}…`);
    try {
      const qs = new URLSearchParams({ date_from: ap.dateFrom, date_to: ap.dateTo, ...areaParams(appliedArea), ...sortParams }).toString();
      await downloadBlob(`/t/${code}/reports/field-activity/export?${qs}`,
        `field-activity_${ap.dateFrom}_to_${ap.dateTo}.xlsx`);
      toast.success(sortCol ? `Excel report downloaded (sorted by ${sortCol.label.toLowerCase()})` : "Excel report downloaded", { id: tid });
    } catch (e) { toast.error(errMsg(e), { id: tid }); } finally { setExporting(false); }
  };

  // Phase 185: `group` (a supervisor section) scopes the popup to that supervisor's wards via its `sup:` / `unassigned:` key
  const open = (metric, row, group = null) => {
    const scoped = group && group.kind !== "no_ward";
    setDetail({
      metric, dateFrom: ap?.dateFrom || dateFrom, dateTo: ap?.dateTo || dateTo,
      area: scoped ? { ...appliedArea, supervisorId: group.key } : appliedArea,
      areaLabel: scoped ? [appliedLabel, group.label].filter(Boolean).join(" • ") : appliedLabel,
      collectorId: row?.collector_id, collectorName: row?.collector_name,
    });
  };

  const t = data?.totals || {};

  return (
    <div className="space-y-4">
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-end gap-2" testId={`${testIdPrefix}-filter-form`}>
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-10 w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid={`${testIdPrefix}-from-date`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-10 w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid={`${testIdPrefix}-to-date`} />
        </div>
        {!controlled && (
          <div className="min-w-[280px] flex-1 max-w-2xl" data-testid={`${testIdPrefix}-area-filter`}>
            <TerritoryFilterSelects masters={masters} value={area} onChange={setArea} testIdPrefix={`${testIdPrefix}-area`} />
          </div>
        )}
        <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={!dateFrom || !dateTo || rangeInvalid} testId={`${testIdPrefix}-fetch`} />
        {canExport && (   /* Phase 143: hidden for view-only supervisors on the MIS Reports page */
        <Button type="button" variant="outline" className="h-10" onClick={doExport} disabled={exporting || !data || !gate.fetched} data-testid={`${testIdPrefix}-export`}>
          <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Exporting..." : "Export Excel"}
        </Button>
        )}
        <FetchDirtyHint dirty={gate.dirty} testId={`${testIdPrefix}-fetch-dirty`} />
      </FetchForm>
      {rangeInvalid && (
        <p className="text-xs text-destructive" role="alert" data-testid={`${testIdPrefix}-range-error`}>Start date must not be after the end date.</p>
      )}
      {!rangeInvalid && days > 92 && !loading && (
        <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-range-note`}>{days} days selected — any range is allowed; long ranges take a little longer to load and export.</p>
      )}
      {loading && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" role="status" aria-live="polite" data-testid={`${testIdPrefix}-loading-note`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading {appliedDays} day{appliedDays === 1 ? "" : "s"} of field activity{longRange ? " — a long range can take a little while, please wait" : ""}…
        </p>
      )}

      {gate.fetched && appliedLabel && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-area-banner`}>
          <Badge variant="outline" className="gap-1 font-normal bg-accent text-accent-foreground border-transparent"
            data-testid={`${testIdPrefix}-area-badge`}>
            <MapPin className="h-3 w-3" /> Area: {appliedLabel}
          </Badge>
          <span>{appliedArea.supervisorId ? "Showing only activity on properties in the selected supervisors' wards." : "Showing only activity on properties in this area."}</span>
          {data?.area?.staff_scoped && (
            <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-staff-scope`}>
              <Users className="h-3 w-3" />
              Listing {data.area.staff_listed} collector{data.area.staff_listed === 1 ? "" : "s"} mapped to {appliedArea.supervisorId ? "these supervisors' wards" : "this area"}
              {data.area.staff_hidden > 0 && (
                <span data-testid={`${testIdPrefix}-staff-hidden`}>
                  {" "}· {data.area.staff_hidden} from other areas hidden
                </span>
              )}
            </span>
          )}
          <button type="button" className="underline underline-offset-4 hover:text-foreground"
            onClick={() => setArea(EMPTY_AREA)} data-testid={`${testIdPrefix}-area-clear`}>
            Clear area
          </button>
        </div>
      )}

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId={`${testIdPrefix}-fetch-prompt`} compact
            subtitle="Pick the date range (and an area) above, then click Fetch to load per-collector activity." />
        </CardContent></Card>
      ) : !data ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {KPIS.map((k) => (
              <Card key={k.key} className="shadow-sm cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => open(k.metric)} data-testid={`${testIdPrefix}-kpi-card-${k.key}`}
                title={`View ${k.label.toLowerCase()} details`}>
                <CardContent className="p-3">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <k.icon className="h-3.5 w-3.5" /> {k.label}
                  </p>
                  {k.key === "collections" ? (
                    <p className="text-lg font-semibold tabular-nums mt-0.5" data-testid={`${testIdPrefix}-kpi-collections`}>
                      {fmtINR(t.collections_amount || 0)}
                      <span className="text-xs font-normal text-muted-foreground"> ({t.collections_count || 0} rcpt)</span>
                    </p>
                  ) : (
                    <p className="text-lg font-semibold tabular-nums mt-0.5" data-testid={`${testIdPrefix}-kpi-${k.key}`}>{t[k.key] || 0}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {data.reconciliation && (
            <CollectionsReconciliation
              unified={data.reconciliation.unified} credited={data.reconciliation.credited} others={data.reconciliation.others}
              creditedLabel="Credited to listed collectors" rangeLabel={`${fmtDate(ap?.dateFrom)} – ${fmtDate(ap?.dateTo)}`}
              collectionsHref={collectionsHref ? collectionsHref(ap, appliedArea) : undefined}
              testId={`${testIdPrefix}-reconciliation`} compact />
          )}

          {data.grouped ? (
            <SupervisorSections code={code} groups={data.groups || []} sort={sort} setSort={setSort} sortCol={sortCol}
              expandable={expandable} drill={drill} open={open} appliedLabel={appliedLabel} staffScoped={!!data?.area?.staff_scoped}
              testIdPrefix={testIdPrefix} />
          ) : (
          <Card className="shadow-sm">
            <CardContent className="p-0">
              {sortCol && <SortBanner sort={sort} sortCol={sortCol} onReset={() => setSort(null)} testIdPrefix={testIdPrefix} />}
              <Table data-testid={`${testIdPrefix}-table`}>
                <TableHeader>
                  <SortHeaderRow sort={sort} setSort={setSort} testIdPrefix={testIdPrefix} />
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8" data-testid={`${testIdPrefix}-empty`}>
                        {data?.area?.staff_scoped
                          ? `No collectors are mapped to ${appliedLabel}. Map collectors to its wards under Users & Staff to see them here.`
                          : appliedLabel ? "No collector activity in the selected area for this period." : "No collectors in scope for this period."}
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <CollectorRow key={r.collector_id} code={code} r={r} prefix={testIdPrefix} expandable={expandable} drill={drill} open={open} />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          )}
        </>
      )}

      <FieldActivityDetailDialog code={code} target={detail} onOpenChange={setDetail} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Table building blocks (shared by the flat list and the Phase 185 supervisor sections)
const slugOf = (s) => String(s || "").replace(/[^a-zA-Z0-9_-]+/g, "-");
const cellBtn = "underline decoration-dotted underline-offset-4 hover:text-primary cursor-pointer";

function SortBanner({ sort, sortCol, onReset, testIdPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b text-xs text-muted-foreground" data-testid={`${testIdPrefix}-sort-banner`}>
      {sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
      <span>Sorted by <span className="font-medium text-foreground">{sortCol.label}</span> ({sort.dir === "asc" ? "ascending" : "descending"}) — the Excel export follows this order.</span>
      <button type="button" className="underline underline-offset-4 hover:text-foreground" onClick={onReset} data-testid={`${testIdPrefix}-sort-clear`}>
        Reset sort
      </button>
    </div>
  );
}

function SortHeaderRow({ sort, setSort, testIdPrefix }) {
  return (
    <TableRow>
      {SORT_COLUMNS.map((col) => {
        const active = sort?.key === col.key;
        const dir = active ? sort.dir : null;
        const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
        const next = nextSort(sort, col);
        const hint = next ? `Sort ${next.dir === "asc" ? "ascending" : "descending"}` : "Reset to default order";
        return (
          <TableHead key={col.key} className={col.align === "right" ? "text-right" : ""}
            aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}>
            <button type="button" onClick={() => setSort(nextSort(sort, col))} title={hint}
              className={`inline-flex items-center gap-1 rounded px-1 -mx-1 h-8 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${col.align === "right" ? "flex-row-reverse" : ""} ${active ? "text-foreground font-semibold" : ""}`}
              data-testid={`${testIdPrefix}-sort-${col.key}`} data-sort-dir={dir || "none"}>
              {col.label}
              <Icon className={`h-3.5 w-3.5 ${active ? "text-primary" : "text-muted-foreground/60"}`} aria-hidden="true" />
            </button>
          </TableHead>
        );
      })}
    </TableRow>
  );
}

/** One collector row (+ its drill-down rows when expanded). `group` (Phase 185) scopes the drill-down and the detail
 *  popups to that supervisor section; `prefix` keeps test ids unique per section. */
function CollectorRow({ code, r, prefix, expandable, drill, open, group = null }) {
  const scopeKey = group?.key || "";
  const scopeParams = group && group.kind !== "no_ward" ? { supervisor_id: group.key } : null;
  const canDrill = expandable && !(group && group.kind === "no_ward");
  const cKey = scopeKey ? `c|${scopeKey}|${r.collector_id}` : `c|${r.collector_id}`;
  const cOpen = canDrill && drill.isOpen(cKey);
  return (
    <React.Fragment>
      <TableRow data-testid={`${prefix}-row-${r.collector_name}`} data-open={canDrill ? (cOpen ? "true" : "false") : undefined}
        className={cOpen ? "bg-muted/10" : undefined}>
        <TableCell className="font-medium text-sm">
          {canDrill ? (
            <button type="button" aria-expanded={cOpen} onClick={() => drill.toggle(cKey)}
              title={cOpen ? "Hide ward-wise breakup" : "Show ward-wise breakup"}
              className="inline-flex items-center gap-1.5 rounded-md -ml-1 pl-1 pr-2 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`${prefix}-expand-collector-${r.collector_id}`}>
              <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 ${cOpen ? "rotate-90" : ""}`} aria-hidden="true" />
              <StaffLabel code={code} userId={r.collector_id} name={r.collector_name} />
            </button>
          ) : (
            <StaffLabel code={code} userId={r.collector_id} name={r.collector_name} />
          )}
          {group && r.mapped === false && (
            <span className="ml-2 text-[11px] text-muted-foreground" title="Not mapped to this supervisor's wards — listed because of activity there"
              data-testid={`${prefix}-activity-only-${r.collector_id}`}>· activity only</span>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          <span className={cellBtn} onClick={() => open("visits", r, group)} data-testid={`${prefix}-cell-visits-${r.collector_name}`}>{r.visits}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          <span className={cellBtn} onClick={() => open("collections", r, group)} data-testid={`${prefix}-cell-receipts-${r.collector_name}`}>{r.collections_count}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums font-medium">
          <span className={cellBtn} onClick={() => open("collections", r, group)} data-testid={`${prefix}-cell-amount-${r.collector_name}`}>{fmtINR(r.collections_amount)}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          <span className={cellBtn} onClick={() => open("surveys", r, group)} data-testid={`${prefix}-cell-surveys-${r.collector_name}`}>{r.new_surveys}</span>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          <span className={cellBtn} onClick={() => open("reassessments", r, group)} data-testid={`${prefix}-cell-reassess-${r.collector_name}`}>{r.reassessments}</span>
        </TableCell>
      </TableRow>
      {cOpen && (
        <CollectorBreakupRows drill={drill} collectorId={r.collector_id} colSpan={6} testIdPrefix={prefix}
          scopeKey={scopeKey} scopeParams={scopeParams} />
      )}
    </React.Fragment>
  );
}

const SECTION_METRICS = [
  { key: "visits", metric: "visits", label: "Visits", fmt: (v) => v || 0 },
  { key: "collections_count", metric: "collections", label: "Receipts", fmt: (v) => v || 0 },
  { key: "collections_amount", metric: "collections", label: "Collected", fmt: (v) => fmtINR(v || 0) },
  { key: "new_surveys", metric: "surveys", label: "New surveys", fmt: (v) => v || 0 },
  { key: "reassessments", metric: "reassessments", label: "Reassessments", fmt: (v) => v || 0 },
];

/** Phase 185 — Supervisor filter applied or 2+ zones selected: one card per supervisor (Monthly Performance layout) —
 *  heading with name, zone(s) and the combined figures, then that supervisor's collectors with the usual columns.
 *  Wards no supervisor owns form a "GESIL-<ZONE> (Unassigned)" card. */
function SupervisorSections({ code, groups, sort, setSort, sortCol, expandable, drill, open, appliedLabel, staffScoped, testIdPrefix }) {
  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-sections`} data-section-count={groups.length}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-grouped-note`}>
        <Users className="h-3.5 w-3.5" />
        <span>Grouped by supervisor — each collector is listed under the supervisor(s) whose wards they are mapped to, with the figures of those wards only.</span>
      </div>
      {sortCol && (
        <Card className="shadow-sm"><CardContent className="p-0">
          <SortBanner sort={sort} sortCol={sortCol} onReset={() => setSort(null)} testIdPrefix={testIdPrefix} />
        </CardContent></Card>
      )}
      {groups.length === 0 && (
        <Card className="shadow-sm"><CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
          {staffScoped
            ? `No collectors are mapped to ${appliedLabel}. Map collectors to its wards under Users & Staff to see them here.`
            : "No supervisors or collectors in scope for this period."}
        </CardContent></Card>
      )}
      {groups.map((g) => {
        const s = slugOf(g.key);
        const prefix = `${testIdPrefix}-${s}`;
        const rows = sortRows(g.rows || [], sort);
        return (
          <Card key={g.key} className="shadow-sm overflow-hidden" data-testid={`${testIdPrefix}-section-${s}`} data-kind={g.kind}>
            <CardContent className="p-0">
              <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-3 min-w-0">
                  {g.kind === "supervisor" ? (
                    <StaffAvatar code={code} userId={g.supervisor_id} name={g.name} size="md" testId={`${prefix}-avatar`} />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0" aria-hidden="true">
                      <MapPinOff className="h-4 w-4" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium flex items-center gap-2 flex-wrap" data-testid={`${prefix}-title`}>
                      {g.kind === "supervisor"
                        ? <StaffLabel code={code} userId={g.supervisor_id} name={g.name} empCode={g.employee_id} />
                        : <span className="font-mono text-sm">{g.label}</span>}
                      {(g.zone_names || []).map((z) => (
                        <Badge key={z} variant="outline" className="text-[10px] gap-0.5 font-normal" data-testid={`${prefix}-zone`}><MapPin className="h-2.5 w-2.5" />{z}</Badge>
                      ))}
                      {g.kind === "unassigned" && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">No supervisor</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`${prefix}-meta`}>
                      {g.collector_count} collector{g.collector_count === 1 ? "" : "s"}
                      {g.ward_count ? ` • ${g.ward_count} ward${g.ward_count === 1 ? "" : "s"}` : ""}
                      {g.shared_wards ? ` • ${g.shared_wards} shared with another supervisor` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 sm:ml-auto" data-testid={`${prefix}-totals`}>
                  {SECTION_METRICS.map((m) => (
                    <button key={m.key} type="button" onClick={() => open(m.metric, null, g)}
                      className="text-right rounded px-1 -mx-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      title={`View ${m.label.toLowerCase()} for this section`} data-testid={`${prefix}-total-${m.key}`}>
                      <p className="text-[11px] text-muted-foreground leading-tight">{m.label}</p>
                      <p className="text-sm font-semibold tabular-nums leading-tight">{m.fmt(g.totals?.[m.key])}</p>
                    </button>
                  ))}
                </div>
              </div>
              <Table data-testid={`${prefix}-table`}>
                <TableHeader>
                  <SortHeaderRow sort={sort} setSort={setSort} testIdPrefix={prefix} />
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6" data-testid={`${prefix}-empty`}>
                        No collectors mapped to these wards.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <CollectorRow key={r.collector_id} code={code} r={r} prefix={prefix} expandable={expandable} drill={drill} open={open} group={g} />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
